// Signing this tab in from the classic site: the login's hand-off (?k= on /submission/flow/continue),
// and the classic site's own session in this browser (step 1's "Continue as").
//
// The same calls as UserService.bridgeAuthentication / seamlessAuthentication (auth/jwt/bridge or
// auth/jwt/seamless, then auth/user/me), with one difference: nothing is written outside this
// tab's sessionStorage. Those also save the token, the user and the session key in cwb-react's
// localStorage, where they outlive the tab: the next visitor on that computer got the previous
// account's language, and an agent's or casting account's token stayed behind after the flow
// refused it (decided 2026-10-30).
//
// Actors only. The login sends an agent or a casting account to their own site, never back here;
// a key or a session of theirs is refused before anything else is asked or kept.

import {
  actorClientIdOf,
  getSubmissionAuth,
  ISubmissionAccountInfo,
  secondsLeftOf,
  setSubmissionAuthFromToken,
} from "../../../helpers/submissionAuth";
import { IAccountNameDto, IClassicSessionDto, ITabSignInDto } from "./types";

const apiBase = (): string => (process.env.REACT_APP_Api || "").replace(/\/+$/, "");

const stripBearerPrefix = (token: string): string => token.replace(/^Bearer\s+/i, "");

// The account's languageChoice as an i18n key: helpers' localeKeys the other way round (the test
// keeps them equal). Not imported from helpers, whose module sets i18next up as it loads, and
// everything that loads the flow's API would load that too.
export const LANGUAGE_CHOICES: { [choice: number]: string } = { 0: "en", 1: "fr", 2: "en_uk", 3: "es", 99: "nb" };

// What the account says about itself: its saved language (an i18n key, "fr") and its name.
// Empty when it can't be read; signing in doesn't wait on it.
const accountOf = async (token: string): Promise<ISubmissionAccountInfo> => {
  try {
    const res = await fetch(`${apiBase()}/auth/user/me`, {
      headers: { Authorization: `Bearer ${stripBearerPrefix(token)}` },
    });
    if (!res.ok) return {};
    const user = await res.json();
    if (!user) return {};
    return {
      language: LANGUAGE_CHOICES[Number(user.languageChoice)],
      firstName: typeof user.firstName === "string" ? user.firstName.trim() : undefined,
      lastName: typeof user.lastName === "string" ? user.lastName.trim() : undefined,
    };
  } catch (e) {
    return {};
  }
};

// A token from one of the sign-in service's exchanges, or "" when it answered anything else.
const tokenFrom = async (path: string, init: RequestInit): Promise<string> => {
  try {
    const res = await fetch(`${apiBase()}${path}`, init);
    if (!res.ok) return "";
    const body = await res.json();
    return body && body.accessToken ? stripBearerPrefix(String(body.accessToken)) : "";
  } catch (e) {
    return "";
  }
};

// Returns the actor's client id once the tab is signed in as them, or 0: the key didn't work, or
// it belongs to someone who isn't an actor.
export const handOffSignIn = async (k: string): Promise<number> => {
  if (!k) return 0;
  const token = await tokenFrom("/auth/jwt/bridge", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ k, appKey: "cwb-react" }),
  });
  if (!actorClientIdOf(token)) return 0;
  return setSubmissionAuthFromToken(token, await accountOf(token));
};

// The classic site's session in this browser (its WebSessionKey cookie, sent to the API with the
// request), when it is an actor's. Kept in memory only until step 1's Continue uses it.
let classicActor: { token: string; account: ISubmissionAccountInfo } | null = null;

export const findClassicActor = async (): Promise<IClassicSessionDto | null> => {
  classicActor = null;
  const token = await tokenFrom("/auth/jwt/seamless", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", CWB: "1" },
    body: JSON.stringify({ appKey: "cwb-react" }),
  });
  const clientId = actorClientIdOf(token);
  if (!clientId) return null;
  const account = await accountOf(token);
  // No name to show: "Continue as" can't say who, so step 1 stays the usual form.
  if (!account.firstName) return null;
  classicActor = { token, account };
  return { clientId, firstName: account.firstName, lastName: account.lastName || "" };
};

// Step 1's Continue: the session found above, as this tab's sign-in. Throws when there is none
// (it ended since, or step 1 never found one).
export const continueClassicActor = async (): Promise<ITabSignInDto> => {
  const found = classicActor || (await findClassicActor().then(() => classicActor));
  if (!found) throw new Error("No classic session to continue.");
  classicActor = null;
  return {
    clientId: actorClientIdOf(found.token),
    jwt: `Bearer ${found.token}`,
    expiresIn: secondsLeftOf(found.token),
    ...found.account,
  };
};

// The name of the account this tab is signed in as.
export const accountNameOfTab = async (): Promise<IAccountNameDto | null> => {
  const auth = getSubmissionAuth();
  if (!auth || !auth.jwt) return null;
  const account = await accountOf(auth.jwt);
  return account.firstName ? { firstName: account.firstName, lastName: account.lastName || "" } : null;
};
